import React, { useRef, useState } from 'react';
import { css, Global } from '@emotion/react';
import { useRouter } from 'next/router';
import { Faq } from '~/shared/components/Summary/components/Faq';
import { SummaryDesktop } from '~/shared/components/Summary/components/SummaryDesktop';
import { USPs } from '~/shared/components/Summary/components/USPs';
import { useCommerceAPI } from '~/features/commerce-api/contexts';
import useCustomer from '~/features/commerce-api/hooks/useCustomer';
import { Modules, ReceiptPage as ReceiptPageProps } from '~/lib/data-contract';
import { transactionKeys } from '~/lib/data-contract/payment';
import { Order, Product, Shipment } from '~/lib/data-contract/salesforce';
import { Container } from '~/shared/components/Container';
import { Text } from '~/shared/components/Text';
import { useEvents } from '~/shared/hooks/useEvents';
import { _transformProductToDataLayer } from '~/shared/hooks/useEvents/helpers';
import {
    _matchCouponsFromCartWithProducts,
    _getCouponsFromOrder,
} from '~/shared/hooks/useEvents/useGTMEvents/helper';
import { useUtils } from '~/shared/hooks/useUtils';
import { useFrame, useMarket } from '~/shared/utils';
import { useTranslation } from '~/shared/utils/translation';
import { interpolate } from '~/shared/utils/translation/utils/interpolateText';
import { DynamicBlockList } from '~/templates/blocks';
import { theme } from '~/theme';
import Spacer from '~/theme/design-system/components/Spacer';
import { CustomerService } from './components/CustomerService';
import { ReceiptData } from './components/ReceiptData';
import { SummaryList } from './components/SummaryList';
import { Header, HeaderText, PriceItem, PriceList, StyledGrid, SummeryWrapper } from './styled';
import { usePaymentDetails } from '~/features/commerce-api/hooks/usePaymentDetails';
import { extractPaymentInstrumentByMethod } from '~/services/altapay/lib/utils';
import { PaymentMethod } from '~/shared/constants';
import { UspFaqWrapper } from '~/shared/components/UspFaqWrapper/UspFaqWrapper';

type ShipmentItem = {
    productItems: (Product & { quantity?: number })[][];
    shipment: Shipment;
};
type Shipments = {
    [key: string]: ShipmentItem;
};

export const ReceiptPage = (props: ReceiptPageProps) => {
    const { pageElements, bottomPageElements } = props;
    const { translate } = useTranslation();
    const { query, replace } = useRouter();
    const orderNo = query?.[transactionKeys.orderNo] as string;
    const trackId = query?.[transactionKeys.trackId] as string;
    const { data: frame } = useFrame();
    const { addPaymentInfoEvent } = useEvents(frame);
    const { convertPriceToLocale } = useUtils();
    const { shopperOrders, shopperProducts } = useCommerceAPI();
    const { isPlusMember, isStorePersonnel, isInitialized } = useCustomer();
    const { purchaseEvent, isReady } = useEvents(frame);
    const { isoCulture } = useMarket();
    const [order, setOrder] = useState<Order | undefined>(undefined);
    const [shipments, setShipments] = useState<Shipments | undefined>(undefined);
    const isFetchingData = useRef(false);
    const {
        market: { receipt },
    } = frame;

    const { basePrice, remainingTotal, discountPrice, giftcardUsage } = usePaymentDetails(
        order,
        false,
    );

    const getOrder = async () => {
        if (orderNo) {
            try {
                const order = (await shopperOrders?.getOrder({
                    parameters: { orderNo: orderNo },
                })) as Order;

                if (!order) {
                    replace(frame.settings?.pageReferences.notFoundErrorPage?.url || '/');
                    return;
                }

                setOrder(order);

                if (order?.productItems?.length) {
                    const products = await shopperProducts?.getProducts({
                        parameters: {
                            ids: order.productItems.map((x) => x.productId).join(','),
                            locale: isoCulture,
                            currency: order.currency,
                        },
                    });

                    const getQuantity = (product: Product) => {
                        const item = order?.productItems?.find(
                            (item) => item.productId === product.id,
                        );

                        return item?.quantity ?? 0;
                    };

                    if (trackId) {
                        if (products?.data?.length) {
                            const orderCoupons = _getCouponsFromOrder(order);
                            const couponCodes = _matchCouponsFromCartWithProducts(
                                order?.productItems,
                                order?.couponItems,
                            );

                            const altaPayInstrument = extractPaymentInstrumentByMethod(
                                order.paymentInstruments,
                                PaymentMethod.ALTAPAY,
                            );

                            purchaseEvent(
                                {
                                    storeId: order.c_pickupStoreID,
                                    storeName: order?.c_pickupStoreID ?? '',
                                    isLoggedIn: isPlusMember,
                                    // TODO(IMC-1523): Replace with more specific payment option.
                                    paymentMethod: altaPayInstrument
                                        ? PaymentMethod.ALTAPAY
                                        : order.c_paymentData?.paymentMethod,
                                    isStorePersonnel,
                                },
                                orderCoupons,
                                orderNo,
                                products?.data?.map((product) => {
                                    product.coupon =
                                        couponCodes?.appliedCoupons[product.productId]?.couponCode;
                                    return _transformProductToDataLayer(
                                        product,
                                        getQuantity(product),
                                    );
                                }),
                                order.orderTotal ?? 0,
                                order.shippingTotal ?? 0,
                            );

                            if (order?.paymentInstruments) {
                                addPaymentInfoEvent(
                                    products?.data?.map((product) => {
                                        product.coupon =
                                            couponCodes?.appliedCoupons[
                                                product.productId
                                            ]?.couponCode;
                                        return _transformProductToDataLayer(
                                            product,
                                            getQuantity(product),
                                        );
                                    }),
                                    order?.orderTotal?.toString() ?? '',
                                    orderCoupons,
                                    // TODO(IMC-1523): Replace with more specific payment option (and card type).
                                    (altaPayInstrument
                                        ? PaymentMethod.ALTAPAY
                                        : order?.paymentInstruments?.[0]?.paymentCard?.cardType) ??
                                        '',
                                );
                            }
                        }

                        if (frame.settings?.pageReferences.receiptPage?.url) {
                            const params = new URLSearchParams(location.search);
                            params.delete('trackId');

                            replace(
                                `${
                                    frame.settings?.pageReferences.receiptPage?.url
                                }/?${params.toString()}`,
                                undefined,
                                {
                                    shallow: true,
                                },
                            );
                        }
                    }

                    const shipments = order?.productItems.reduce((acc, item) => {
                        const { shipmentId } = item;
                        const shipment = order?.shipments?.find(
                            (shipment) => shipment.shipmentId === shipmentId,
                        );

                        const product = products?.data.find(
                            (product) => product.id === item.productId,
                        );

                        if (shipmentId) {
                            if (!(shipmentId in acc)) {
                                acc[shipmentId] = {
                                    productItems: [],
                                    shipment,
                                };
                            }

                            acc[shipmentId].productItems.push({
                                ...product,
                                quantity: item.quantity,
                            });
                        }

                        return acc;
                    }, {});

                    setShipments(shipments);
                }
            } catch (Error) {
                replace(frame.settings?.pageReferences.notFoundErrorPage?.url || '/');
            }
        }
    };

    if (isInitialized && isReady && !order && orderNo && !isFetchingData.current) {
        isFetchingData.current = true;
        getOrder();
    }

    return (
        <>
            <Global
                styles={css`
                    #__next > :not(main) {
                        @media print {
                            display: none;
                        }
                    }
                `}
            />

            <DynamicBlockList elements={pageElements as Modules[]} />
            <Container hasVerticalGutter hasGutter>
                <StyledGrid flow="column" columnGap="small">
                    <div>
                        <Header>{translate('checkout.receipt.receipt')}</Header>
                        <HeaderText
                            html={interpolate(receipt?.header ?? '', {
                                email: order?.customerInfo?.email ?? '',
                            })}
                        />

                        <ReceiptData order={order} />

                        {shipments && (
                            <>
                                {Object.entries(shipments ?? []).map(([_, item], index) => (
                                    <>
                                        <Spacer space={theme.space[6]} />
                                        <Text as="h3" variant="display4">
                                            {translate('checkout.splitdelivery.title', {
                                                current: String(index + 1),
                                                total: String(
                                                    Object.entries(shipments ?? []).length,
                                                ),
                                            })}
                                        </Text>
                                        <Spacer space={theme.space[2]} />
                                        <Text as="span" variant="bodySmall">
                                            {translate('checkout.receipt.deliveryOf2', {
                                                amount: String(item.productItems.length),
                                                total: String(order?.productItems?.length),
                                            })}
                                            {item.shipment?.shippingMethod?.c_shippingType ===
                                                'TRUCK' && (
                                                <p>
                                                    {translate(
                                                        `checkout.splitdelivery.truckDelivery`,
                                                    )}
                                                </p>
                                            )}

                                            {item?.shipment?.shippingMethod
                                                ?.c_estimatedArrivalTime && (
                                                <>
                                                    <Spacer
                                                        variant="horizontal"
                                                        space={theme.space[1]}
                                                    />
                                                    <span>&sdot;</span>
                                                    {
                                                        item?.shipment?.shippingMethod
                                                            ?.c_estimatedArrivalTime
                                                    }
                                                </>
                                            )}
                                        </Text>
                                        <Spacer space={theme.space[2]} />
                                        <SummeryWrapper>
                                            <SummaryList
                                                products={item.productItems}
                                                productItems={order?.productItems}
                                                large
                                            ></SummaryList>
                                        </SummeryWrapper>
                                    </>
                                ))}

                                <PriceList>
                                    <PriceItem>
                                        <p>{translate('checkout.priceSection.price')}</p>
                                        <p>{convertPriceToLocale(basePrice)}</p>
                                    </PriceItem>
                                    {!!discountPrice && (
                                        <PriceItem type="discount">
                                            <p>{translate('checkout.priceSection.discount')}</p>
                                            <p>{'-' + convertPriceToLocale(discountPrice)}</p>
                                        </PriceItem>
                                    )}
                                    {!!order?.c_totalMemberDiscount && (
                                        <PriceItem type="discount">
                                            <p>{translate('checkout.receipt.memberDiscount')}</p>
                                            <p>
                                                {'-' +
                                                    convertPriceToLocale(
                                                        order.c_totalMemberDiscount,
                                                    )}
                                            </p>
                                        </PriceItem>
                                    )}
                                    <PriceItem>
                                        <p>{translate('checkout.priceSection.shipping')}</p>
                                        <p>{convertPriceToLocale(order?.shippingTotal)}</p>
                                    </PriceItem>
                                    {!!giftcardUsage && (
                                        <PriceItem type="discount">
                                            <p>
                                                {translate('checkout.giftCertificateForm.cardName')}
                                            </p>
                                            <p>{'-' + convertPriceToLocale(giftcardUsage)}</p>
                                        </PriceItem>
                                    )}
                                    <PriceItem>
                                        <p>{translate('basket.priceWithTax')}</p>
                                        <p>{convertPriceToLocale(remainingTotal)}</p>
                                    </PriceItem>
                                </PriceList>
                            </>
                        )}
                    </div>
                    <div
                        css={{
                            '@media print': { display: 'none' },
                        }}
                    >
                        <SummaryDesktop>
                            {!!frame.market.receipt && (
                                <CustomerService data={frame.market.receipt} />
                            )}
                            <UspFaqWrapper>
                                {!!frame.market?.checkoutUsps && (
                                    <USPs usps={frame.market?.checkoutUsps} />
                                )}
                                {!!frame.market?.checkoutFaq?.length && (
                                    <Faq
                                        title={translate('checkout.faqSection.title')}
                                        faqs={frame.market?.checkoutFaq}
                                    />
                                )}
                            </UspFaqWrapper>
                        </SummaryDesktop>
                    </div>
                </StyledGrid>
            </Container>
            <DynamicBlockList elements={bottomPageElements as Modules[]} />
        </>
    );
};
